import { useId, useRef } from 'react';
import type { KeyboardEvent, ReactNode } from 'react';

/**
 * Schlanke Tabs-Komponente nach WAI-ARIA Tabs-Pattern.
 *
 *  - Pfeiltasten (links/rechts) zum Wechseln (Roving-Tabindex)
 *  - Home / End für ersten / letzten Tab
 *  - Disabled-Tabs werden übersprungen
 *  - Panels sind selektiv gerendert (Lazy: nur aktives Panel im DOM)
 *
 * Bewusst NICHT auf @radix-ui/react-tabs aufgebaut - kein Dependency,
 * 60 Zeilen, keine Bundle-Bloat.
 */

export interface TabItem<T extends string> {
  id: T;
  label: ReactNode;
  disabled?: boolean;
  /** Optionaler Hint rechts vom Label, z.B. Sprint-Badge. */
  trailing?: ReactNode;
}

interface TabsProps<T extends string> {
  items: TabItem<T>[];
  active: T;
  onChange: (id: T) => void;
  ariaLabel: string;
  children: ReactNode;
  className?: string;
}

export function Tabs<T extends string>({
  items,
  active,
  onChange,
  ariaLabel,
  children,
  className,
}: TabsProps<T>): JSX.Element {
  const idPrefix = useId();
  const tablistRef = useRef<HTMLDivElement>(null);

  const focusable = items.filter((t) => !t.disabled);

  const handleKey = (event: KeyboardEvent<HTMLDivElement>): void => {
    const idx = focusable.findIndex((t) => t.id === active);
    if (idx === -1) return;
    let nextIdx = idx;
    switch (event.key) {
      case 'ArrowRight':
        nextIdx = (idx + 1) % focusable.length;
        break;
      case 'ArrowLeft':
        nextIdx = (idx - 1 + focusable.length) % focusable.length;
        break;
      case 'Home':
        nextIdx = 0;
        break;
      case 'End':
        nextIdx = focusable.length - 1;
        break;
      default:
        return;
    }
    event.preventDefault();
    const next = focusable[nextIdx];
    if (!next) return;
    onChange(next.id);
    const tab = tablistRef.current?.querySelector<HTMLButtonElement>(
      `[data-tab-id="${next.id}"]`
    );
    tab?.focus();
  };

  return (
    <div className={className}>
      <div
        ref={tablistRef}
        role="tablist"
        tabIndex={-1}
        aria-label={ariaLabel}
        onKeyDown={handleKey}
        className="flex flex-wrap gap-1 border-b border-border"
      >
        {items.map((tab) => {
          const isActive = tab.id === active;
          const tabId = `${idPrefix}-tab-${tab.id}`;
          const panelId = `${idPrefix}-panel-${tab.id}`;
          return (
            <button
              key={tab.id}
              id={tabId}
              type="button"
              role="tab"
              data-tab-id={tab.id}
              aria-selected={isActive}
              aria-controls={panelId}
              tabIndex={isActive ? 0 : -1}
              disabled={tab.disabled}
              onClick={() => !tab.disabled && onChange(tab.id)}
              className={[
                'inline-flex items-center gap-2 border-b-2 px-3 py-2 text-sm transition-colors',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
                isActive
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted hover:text-text',
                tab.disabled ? 'cursor-not-allowed opacity-50' : '',
              ].join(' ')}
            >
              <span>{tab.label}</span>
              {tab.trailing}
            </button>
          );
        })}
      </div>
      <div
        role="tabpanel"
        id={`${idPrefix}-panel-${active}`}
        aria-labelledby={`${idPrefix}-tab-${active}`}
        className="pt-4"
      >
        {children}
      </div>
    </div>
  );
}
